/* eslint-disable i18next/no-literal-string */
import type { Enums } from '@aikoa/common'
import { useTranslations } from '@aikoa/i18n/react'
import { useQuery } from '@tanstack/react-query'
import { Link } from 'expo-router'
import { useState } from 'react'
import { Pressable, View } from 'react-native'
import { useDebouncedCallback } from 'use-debounce'
import ContentBox from '~/components/layout/ContentBox'
import ScreenView from '~/components/layout/ScreenView'
import { Body, Label } from '~/components/typography/Content'
import { Heading2 } from '~/components/typography/Titles'
import { Button, ButtonLabel } from '~/components/ui/button'
import { Input } from '~/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger } from '~/components/ui/select'
import { Text } from '~/components/ui/text'
import { trpc } from '~/lib/trpc'

type ContactRequestStatus = Enums<'contact_request_status'>

type ContactRequest = {
  id: string
  shortId: string
  status: ContactRequestStatus
  sentAt: string | null
  respondedAt: string | null
  createdAt: string
  contactName: string | null
  contactEmail: string | null
  contactPhone: string | null
  assetName: string | null
  senderName: string | null
  senderUserId: string | null
  responderName: string | null
  responderUserId: string | null
}

function getStatusLabel(
  status: ContactRequestStatus,
  t: ReturnType<typeof useTranslations<'admin'>>['t']
): string {
  const statusMap: Record<ContactRequestStatus, string> = {
    pending: t(($) => $.contactRequests.statuses.pending),
    accepted: t(($) => $.contactRequests.statuses.accepted),
    rejected: t(($) => $.contactRequests.statuses.rejected),
  }
  return statusMap[status]
}

function getStatusColor(status: ContactRequestStatus): string {
  switch (status) {
    case 'pending': {
      return 'bg-yellow-100 text-yellow-800'
    }
    case 'accepted': {
      return 'bg-green-100 text-green-800'
    }
    case 'rejected': {
      return 'bg-red-100 text-red-800'
    }
  }
}

function formatDate(dateString: string | null): string {
  if (!dateString) {
    return '-'
  }
  return new Date(dateString).toLocaleDateString('fi-FI', {
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  })
}

function ContactRequestRow({ contactRequest }: { contactRequest: ContactRequest }) {
  const { t } = useTranslations('admin')

  return (
    <Link
      href={
        contactRequest.senderUserId
          ? `/(authenticated)/admin/users/${contactRequest.senderUserId}`
          : '/(authenticated)/admin/contact-requests'
      }
      asChild>
      <Pressable className="gap-3 rounded-lg border border-neutral-200 bg-white p-4 active:bg-neutral-50">
        <View className="flex-row items-start justify-between gap-4">
          <View className="flex-1 gap-1">
            <Text className="font-medium text-base">
              {contactRequest.contactName ?? t(($) => $.contactRequests.unknownContact)}
            </Text>

            {!!contactRequest.senderName && (
              <Label className="text-sm text-secondary">
                {t(($) => $.contactRequests.sentBy, {
                  name: contactRequest.senderName,
                })}
              </Label>
            )}

            {!!contactRequest.assetName && (
              <Label className="text-sm text-secondary">{contactRequest.assetName}</Label>
            )}
          </View>

          <View className={`rounded-md px-3 py-1.5 ${getStatusColor(contactRequest.status)}`}>
            <Text className="font-medium text-sm">{getStatusLabel(contactRequest.status, t)}</Text>
          </View>
        </View>

        <View className="flex-row flex-wrap gap-4">
          <View className="gap-0.5">
            <Label className="text-xs text-neutral-400">
              {t(($) => $.contactRequests.fields.created)}
            </Label>
            <Label className="text-sm text-secondary">{formatDate(contactRequest.createdAt)}</Label>
          </View>

          {!!contactRequest.sentAt && (
            <View className="gap-0.5">
              <Label className="text-xs text-neutral-400">
                {t(($) => $.contactRequests.fields.sent)}
              </Label>
              <Label className="text-sm text-secondary">{formatDate(contactRequest.sentAt)}</Label>
            </View>
          )}

          {!!contactRequest.respondedAt && (
            <View className="gap-0.5">
              <Label className="text-xs text-neutral-400">
                {t(($) => $.contactRequests.fields.responded)}
              </Label>
              <Label className="text-sm text-secondary">
                {formatDate(contactRequest.respondedAt)}
              </Label>
            </View>
          )}
        </View>

        {!!contactRequest.responderName && (
          <Label className="text-sm text-secondary">
            {t(($) => $.contactRequests.respondedBy, {
              name: contactRequest.responderName,
            })}
          </Label>
        )}
      </Pressable>
    </Link>
  )
}

const PAGE_SIZE = 20

type StatusFilterOption = {
  value: ContactRequestStatus | 'all'
  label: string
}

export default function ContactRequests() {
  const { t } = useTranslations('admin')
  const [page, setPage] = useState(1)
  const [searchInput, setSearchInput] = useState('')
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState<StatusFilterOption>({
    value: 'all',
    label: t(($) => $.contactRequests.filters.all),
  })

  const debouncedSetSearch = useDebouncedCallback((value: string) => {
    setSearch(value)
    setPage(1)
  }, 300)

  const handleSearchChange = (value: string) => {
    setSearchInput(value)
    debouncedSetSearch(value)
  }

  const handleStatusChange = (option: StatusFilterOption | undefined) => {
    if (option) {
      setStatusFilter(option)
      setPage(1)
    }
  }

  const queryInput = {
    page,
    limit: PAGE_SIZE,
    search: search || undefined,
    status: statusFilter.value === 'all' ? undefined : (statusFilter.value as ContactRequestStatus),
  }

  const { data, isLoading, error, isFetching } = useQuery(
    trpc.admin.listContactRequests.queryOptions(queryInput)
  )

  const contactRequests = data?.contactRequests ?? []
  const totalPages = data?.totalPages ?? 1
  const totalCount = data?.totalCount ?? 0
  const currentPage = data?.page ?? page

  const canGoBack = currentPage > 1
  const canGoNext = currentPage < totalPages

  const statusOptions: StatusFilterOption[] = [
    { value: 'all', label: t(($) => $.contactRequests.filters.all) },
    { value: 'pending', label: t(($) => $.contactRequests.statuses.pending) },
    { value: 'accepted', label: t(($) => $.contactRequests.statuses.accepted) },
    { value: 'rejected', label: t(($) => $.contactRequests.statuses.rejected) },
  ]

  return (
    <ScreenView>
      <ContentBox className="flex-1" contentContainerClassName="gap-6" variant="transparent">
        <View className="gap-4">
          <Heading2 className="text-center">{t(($) => $.contactRequests.title)}</Heading2>
          <Body className="text-center text-neutral-600">
            {t(($) => $.contactRequests.description)}
          </Body>
        </View>

        <View className="gap-3">
          <Input
            value={searchInput}
            onChangeText={handleSearchChange}
            placeholder={t(($) => $.contactRequests.searchPlaceholder)}
            variant="box"
            className="bg-white"
          />

          <Select
            value={statusFilter}
            onValueChange={(value) => handleStatusChange(value as StatusFilterOption)}>
            <SelectTrigger className="h-12 border-neutral-300 bg-white">
              <Text className="text-sm">{statusFilter.label}</Text>
            </SelectTrigger>
            <SelectContent align="start" sideOffset={4}>
              {statusOptions.map((option) => (
                <SelectItem key={option.value} value={option.value} label={option.label} />
              ))}
            </SelectContent>
          </Select>
        </View>

        {isLoading && (
          <Body className="text-center text-neutral-500">
            {t(($) => $.contactRequests.loading)}
          </Body>
        )}

        {!!error && (
          <Body className="text-center text-red-600">{t(($) => $.contactRequests.loadError)}</Body>
        )}

        {!isLoading && !error && contactRequests.length === 0 && (
          <Body className="text-center text-neutral-500">
            {search || statusFilter.value !== 'all'
              ? t(($) => $.contactRequests.noResults)
              : t(($) => $.contactRequests.none)}
          </Body>
        )}

        {contactRequests.length > 0 && (
          <>
            <View className="gap-3">
              {contactRequests.map((contactRequest) => (
                <ContactRequestRow key={contactRequest.id} contactRequest={contactRequest} />
              ))}
            </View>

            <View className="flex-row items-center justify-between gap-4 pt-2">
              <Button
                variant="outline"
                size="sm"
                disabled={!canGoBack || isFetching}
                onPress={() => setPage(currentPage - 1)}>
                <ButtonLabel>{t(($) => $.contactRequests.previous)}</ButtonLabel>
              </Button>

              <Text className="text-sm text-neutral-600">
                {t(($) => $.contactRequests.page, {
                  current: currentPage,
                  total: totalPages,
                  count: totalCount,
                })}
              </Text>

              <Button
                variant="outline"
                size="sm"
                disabled={!canGoNext || isFetching}
                onPress={() => setPage(currentPage + 1)}>
                <ButtonLabel>{t(($) => $.contactRequests.next)}</ButtonLabel>
              </Button>
            </View>
          </>
        )}
      </ContentBox>
    </ScreenView>
  )
}
